import {
  useMutation,
  useQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import {
  useCallback
} from 'react';

import type {
  AttachmentType,
  GetAttachmentByIdResponse,
  GetAttachmentByTypeResponse,
  GetAttachmentsParams,
  GetAttachmentsResponse,
  ProblemDetails,
  UploadAttachmentContentBody,
  UploadAttachmentContentByTypeBody
} from '../../models';

import { useCustomWelcomeClient } from '../../custom-client.tsx';
import type { ErrorType } from '../../custom-client.tsx';

type AwaitedInput<T> = PromiseLike<T> | T;

      type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;




const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * Returns a list of attachments<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useGetAttachmentsHook = () => {
        const getAttachments = useCustomWelcomeClient<GetAttachmentsResponse>();

        return useCallback((
    id: string,
    params?: GetAttachmentsParams,
 signal?: AbortSignal
) => {


        return getAttachments(
          {url: `/requests/${id}/attachments`, method: 'GET',
        params, signal
    },
          );
        }, [getAttachments])
      }




export const getGetAttachmentsQueryKey = (id: string,
    params?: GetAttachmentsParams,) => {
    return [
    `/requests/${id}/attachments`, ...(params ? [params] : [])
    ] as const;
    }


export const useGetAttachmentsQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useGetAttachmentsHook>>>, TError = ErrorType<ProblemDetails>>(id: string,
    params?: GetAttachmentsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAttachmentsHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAttachmentsQueryKey(id,params);

  const getAttachments =  useGetAttachmentsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetAttachmentsHook>>>> = ({ signal }) => getAttachments(id,params, signal);





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAttachmentsHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetAttachmentsQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetAttachmentsHook>>>>
export type GetAttachmentsQueryError = ErrorType<ProblemDetails>


export function useGetAttachments<TData = Awaited<ReturnType<ReturnType<typeof useGetAttachmentsHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    params: undefined |  GetAttachmentsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAttachmentsHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetAttachmentsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetAttachmentsHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetAttachments<TData = Awaited<ReturnType<ReturnType<typeof useGetAttachmentsHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    params?: GetAttachmentsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAttachmentsHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetAttachmentsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetAttachmentsHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetAttachments<TData = Awaited<ReturnType<ReturnType<typeof useGetAttachmentsHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    params?: GetAttachmentsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAttachmentsHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetAttachments<TData = Awaited<ReturnType<ReturnType<typeof useGetAttachmentsHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    params?: GetAttachmentsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAttachmentsHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useGetAttachmentsQueryOptions(id,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Returns an attachment by id<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useGetAttachmentByIdHook = () => {
        const getAttachmentById = useCustomWelcomeClient<GetAttachmentByIdResponse>();

        return useCallback((
    id: string,
    attachmentId: string,
 signal?: AbortSignal
) => {


        return getAttachmentById(
          {url: `/requests/${id}/attachments/${attachmentId}`, method: 'GET', signal
    },
          );
        }, [getAttachmentById])
      }




export const getGetAttachmentByIdQueryKey = (id: string,
    attachmentId: string,) => {
    return [
    `/requests/${id}/attachments/${attachmentId}`
    ] as const;
    }


export const useGetAttachmentByIdQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useGetAttachmentByIdHook>>>, TError = ErrorType<ProblemDetails>>(id: string,
    attachmentId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAttachmentByIdHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAttachmentByIdQueryKey(id,attachmentId);

  const getAttachmentById =  useGetAttachmentByIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetAttachmentByIdHook>>>> = ({ signal }) => getAttachmentById(id,attachmentId, signal);





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && attachmentId !== null && attachmentId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAttachmentByIdHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetAttachmentByIdQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetAttachmentByIdHook>>>>
export type GetAttachmentByIdQueryError = ErrorType<ProblemDetails>


export function useGetAttachmentById<TData = Awaited<ReturnType<ReturnType<typeof useGetAttachmentByIdHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    attachmentId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAttachmentByIdHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetAttachmentByIdHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetAttachmentByIdHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetAttachmentById<TData = Awaited<ReturnType<ReturnType<typeof useGetAttachmentByIdHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    attachmentId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAttachmentByIdHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetAttachmentByIdHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetAttachmentByIdHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetAttachmentById<TData = Awaited<ReturnType<ReturnType<typeof useGetAttachmentByIdHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    attachmentId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAttachmentByIdHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetAttachmentById<TData = Awaited<ReturnType<ReturnType<typeof useGetAttachmentByIdHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    attachmentId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAttachmentByIdHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useGetAttachmentByIdQueryOptions(id,attachmentId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Deletes the content of an attachment<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useDeleteAttachmentContentHook = () => {
        const deleteAttachmentContent = useCustomWelcomeClient<void>();

        return useCallback((
    id: string,
    attachmentId: string,
 signal?: AbortSignal
) => {


        return deleteAttachmentContent(
          {url: `/requests/${id}/attachments/${attachmentId}/content`, method: 'DELETE', signal
    },
          );
        }, [deleteAttachmentContent])
      }




export const useDeleteAttachmentContentMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useDeleteAttachmentContentHook>>>, TError,DeleteAttachmentContentMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useDeleteAttachmentContentHook>>>, TError,DeleteAttachmentContentMutationVariables, TContext> => {

const mutationKey = ['deleteAttachmentContent'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const deleteAttachmentContent =  useDeleteAttachmentContentHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useDeleteAttachmentContentHook>>>, DeleteAttachmentContentMutationVariables> = (props) => {
          const {id,attachmentId} = props ?? {};

          return  deleteAttachmentContent(id,attachmentId,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteAttachmentContentMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDeleteAttachmentContentHook>>>>

    export type DeleteAttachmentContentMutationError = ErrorType<ProblemDetails>
    export type DeleteAttachmentContentMutationVariables = {id: string;attachmentId: string}

    export const useDeleteAttachmentContent = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useDeleteAttachmentContentHook>>>, TError,DeleteAttachmentContentMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useDeleteAttachmentContentHook>>>,
        TError,
        DeleteAttachmentContentMutationVariables,
        TContext
      > => {
      return useMutation(useDeleteAttachmentContentMutationOptions(options), queryClient);
    }
    /**
 * Returns the content of an attachment<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useDownloadAttachmentContentHook = () => {
        const downloadAttachmentContent = useCustomWelcomeClient<Blob>();

        return useCallback((
    id: string,
    attachmentId: string,
 signal?: AbortSignal
) => {


        return downloadAttachmentContent(
          {url: `/requests/${id}/attachments/${attachmentId}/download`, method: 'GET',
        responseType: 'blob', signal
    },
          );
        }, [downloadAttachmentContent])
      }




export const getDownloadAttachmentContentQueryKey = (id: string,
    attachmentId: string,) => {
    return [
    `/requests/${id}/attachments/${attachmentId}/download`
    ] as const;
    }


export const useDownloadAttachmentContentQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentHook>>>, TError = ErrorType<ProblemDetails>>(id: string,
    attachmentId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadAttachmentContentQueryKey(id,attachmentId);

  const downloadAttachmentContent =  useDownloadAttachmentContentHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentHook>>>> = ({ signal }) => downloadAttachmentContent(id,attachmentId, signal);





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && attachmentId !== null && attachmentId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type DownloadAttachmentContentQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentHook>>>>
export type DownloadAttachmentContentQueryError = ErrorType<ProblemDetails>


export function useDownloadAttachmentContent<TData = Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    attachmentId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useDownloadAttachmentContent<TData = Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    attachmentId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useDownloadAttachmentContent<TData = Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    attachmentId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useDownloadAttachmentContent<TData = Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    attachmentId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useDownloadAttachmentContentQueryOptions(id,attachmentId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Uploads content to an attachment<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useUploadAttachmentContentHook = () => {
        const uploadAttachmentContent = useCustomWelcomeClient<void>();

        return useCallback((
    id: string,
    attachmentId: string,
    uploadAttachmentContentBody: UploadAttachmentContentBody,
 signal?: AbortSignal
) => {

        const formData = new FormData();
if(uploadAttachmentContentBody.File !== undefined) {
 formData.append(`File`, uploadAttachmentContentBody.File);
 }

        return uploadAttachmentContent(
          {url: `/requests/${id}/attachments/${attachmentId}/upload`, method: 'PUT',
      headers: {'Content-Type': 'multipart/form-data', },
       data: formData, signal
    },
          );
        }, [uploadAttachmentContent])
      }




export const useUploadAttachmentContentMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useUploadAttachmentContentHook>>>, TError,UploadAttachmentContentMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useUploadAttachmentContentHook>>>, TError,UploadAttachmentContentMutationVariables, TContext> => {

const mutationKey = ['uploadAttachmentContent'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const uploadAttachmentContent =  useUploadAttachmentContentHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useUploadAttachmentContentHook>>>, UploadAttachmentContentMutationVariables> = (props) => {
          const {id,attachmentId,data} = props ?? {};

          return  uploadAttachmentContent(id,attachmentId,data,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UploadAttachmentContentMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useUploadAttachmentContentHook>>>>
    export type UploadAttachmentContentMutationBody = UploadAttachmentContentBody
    export type UploadAttachmentContentMutationError = ErrorType<ProblemDetails>
    export type UploadAttachmentContentMutationVariables = {id: string;attachmentId: string;data: UploadAttachmentContentBody}

    export const useUploadAttachmentContent = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useUploadAttachmentContentHook>>>, TError,UploadAttachmentContentMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useUploadAttachmentContentHook>>>,
        TError,
        UploadAttachmentContentMutationVariables,
        TContext
      > => {
      return useMutation(useUploadAttachmentContentMutationOptions(options), queryClient);
    }
    /**
 * Returns an attachment by type<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useGetAttachmentByTypeHook = () => {
        const getAttachmentByType = useCustomWelcomeClient<GetAttachmentByTypeResponse>();

        return useCallback((
    id: string,
    attachmentType: AttachmentType,
 signal?: AbortSignal
) => {


        return getAttachmentByType(
          {url: `/requests/${id}/attachments/types/${attachmentType}`, method: 'GET', signal
    },
          );
        }, [getAttachmentByType])
      }




export const getGetAttachmentByTypeQueryKey = (id: string,
    attachmentType: AttachmentType,) => {
    return [
    `/requests/${id}/attachments/types/${attachmentType}`
    ] as const;
    }


export const useGetAttachmentByTypeQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useGetAttachmentByTypeHook>>>, TError = ErrorType<ProblemDetails>>(id: string,
    attachmentType: AttachmentType, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAttachmentByTypeHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAttachmentByTypeQueryKey(id,attachmentType);

  const getAttachmentByType =  useGetAttachmentByTypeHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetAttachmentByTypeHook>>>> = ({ signal }) => getAttachmentByType(id,attachmentType, signal);





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && attachmentType !== null && attachmentType !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAttachmentByTypeHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetAttachmentByTypeQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetAttachmentByTypeHook>>>>
export type GetAttachmentByTypeQueryError = ErrorType<ProblemDetails>


export function useGetAttachmentByType<TData = Awaited<ReturnType<ReturnType<typeof useGetAttachmentByTypeHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    attachmentType: AttachmentType, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAttachmentByTypeHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetAttachmentByTypeHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetAttachmentByTypeHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetAttachmentByType<TData = Awaited<ReturnType<ReturnType<typeof useGetAttachmentByTypeHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    attachmentType: AttachmentType, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAttachmentByTypeHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetAttachmentByTypeHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetAttachmentByTypeHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetAttachmentByType<TData = Awaited<ReturnType<ReturnType<typeof useGetAttachmentByTypeHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    attachmentType: AttachmentType, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAttachmentByTypeHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetAttachmentByType<TData = Awaited<ReturnType<ReturnType<typeof useGetAttachmentByTypeHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    attachmentType: AttachmentType, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAttachmentByTypeHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useGetAttachmentByTypeQueryOptions(id,attachmentType,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Deletes the content of an attachment<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useDeleteAttachmentContentByTypeHook = () => {
        const deleteAttachmentContentByType = useCustomWelcomeClient<void>();

        return useCallback((
    id: string,
    attachmentType: AttachmentType,
 signal?: AbortSignal
) => {


        return deleteAttachmentContentByType(
          {url: `/requests/${id}/attachments/types/${attachmentType}/content`, method: 'DELETE', signal
    },
          );
        }, [deleteAttachmentContentByType])
      }




export const useDeleteAttachmentContentByTypeMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useDeleteAttachmentContentByTypeHook>>>, TError,DeleteAttachmentContentByTypeMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useDeleteAttachmentContentByTypeHook>>>, TError,DeleteAttachmentContentByTypeMutationVariables, TContext> => {

const mutationKey = ['deleteAttachmentContentByType'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const deleteAttachmentContentByType =  useDeleteAttachmentContentByTypeHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useDeleteAttachmentContentByTypeHook>>>, DeleteAttachmentContentByTypeMutationVariables> = (props) => {
          const {id,attachmentType} = props ?? {};

          return  deleteAttachmentContentByType(id,attachmentType,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteAttachmentContentByTypeMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDeleteAttachmentContentByTypeHook>>>>

    export type DeleteAttachmentContentByTypeMutationError = ErrorType<ProblemDetails>
    export type DeleteAttachmentContentByTypeMutationVariables = {id: string;attachmentType: AttachmentType}

    export const useDeleteAttachmentContentByType = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useDeleteAttachmentContentByTypeHook>>>, TError,DeleteAttachmentContentByTypeMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useDeleteAttachmentContentByTypeHook>>>,
        TError,
        DeleteAttachmentContentByTypeMutationVariables,
        TContext
      > => {
      return useMutation(useDeleteAttachmentContentByTypeMutationOptions(options), queryClient);
    }
    /**
 * Returns the content of an attachment<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useDownloadAttachmentContentByTypeHook = () => {
        const downloadAttachmentContentByType = useCustomWelcomeClient<Blob>();

        return useCallback((
    id: string,
    attachmentType: AttachmentType,
 signal?: AbortSignal
) => {


        return downloadAttachmentContentByType(
          {url: `/requests/${id}/attachments/types/${attachmentType}/download`, method: 'GET',
        responseType: 'blob', signal
    },
          );
        }, [downloadAttachmentContentByType])
      }




export const getDownloadAttachmentContentByTypeQueryKey = (id: string,
    attachmentType: AttachmentType,) => {
    return [
    `/requests/${id}/attachments/types/${attachmentType}/download`
    ] as const;
    }


export const useDownloadAttachmentContentByTypeQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentByTypeHook>>>, TError = ErrorType<ProblemDetails>>(id: string,
    attachmentType: AttachmentType, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentByTypeHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadAttachmentContentByTypeQueryKey(id,attachmentType);

  const downloadAttachmentContentByType =  useDownloadAttachmentContentByTypeHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentByTypeHook>>>> = ({ signal }) => downloadAttachmentContentByType(id,attachmentType, signal);





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined && attachmentType !== null && attachmentType !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentByTypeHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type DownloadAttachmentContentByTypeQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentByTypeHook>>>>
export type DownloadAttachmentContentByTypeQueryError = ErrorType<ProblemDetails>


export function useDownloadAttachmentContentByType<TData = Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentByTypeHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    attachmentType: AttachmentType, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentByTypeHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentByTypeHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentByTypeHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useDownloadAttachmentContentByType<TData = Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentByTypeHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    attachmentType: AttachmentType, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentByTypeHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentByTypeHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentByTypeHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useDownloadAttachmentContentByType<TData = Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentByTypeHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    attachmentType: AttachmentType, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentByTypeHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useDownloadAttachmentContentByType<TData = Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentByTypeHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    attachmentType: AttachmentType, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useDownloadAttachmentContentByTypeHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useDownloadAttachmentContentByTypeQueryOptions(id,attachmentType,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Uploads content to an attachment<br><br><b>Required Scopes</b><br>welcome.doka.com/onboarding<br>welcome.doka.com/onboarding-backoffice
 */
export const useUploadAttachmentContentByTypeHook = () => {
        const uploadAttachmentContentByType = useCustomWelcomeClient<void>();

        return useCallback((
    id: string,
    attachmentType: AttachmentType,
    uploadAttachmentContentByTypeBody: UploadAttachmentContentByTypeBody,
 signal?: AbortSignal
) => {

        const formData = new FormData();
if(uploadAttachmentContentByTypeBody.File !== undefined) {
 formData.append(`File`, uploadAttachmentContentByTypeBody.File);
 }

        return uploadAttachmentContentByType(
          {url: `/requests/${id}/attachments/types/${attachmentType}/upload`, method: 'PUT',
      headers: {'Content-Type': 'multipart/form-data', },
       data: formData, signal
    },
          );
        }, [uploadAttachmentContentByType])
      }




export const useUploadAttachmentContentByTypeMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useUploadAttachmentContentByTypeHook>>>, TError,UploadAttachmentContentByTypeMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useUploadAttachmentContentByTypeHook>>>, TError,UploadAttachmentContentByTypeMutationVariables, TContext> => {

const mutationKey = ['uploadAttachmentContentByType'];
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const uploadAttachmentContentByType =  useUploadAttachmentContentByTypeHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useUploadAttachmentContentByTypeHook>>>, UploadAttachmentContentByTypeMutationVariables> = (props) => {
          const {id,attachmentType,data} = props ?? {};

          return  uploadAttachmentContentByType(id,attachmentType,data,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UploadAttachmentContentByTypeMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useUploadAttachmentContentByTypeHook>>>>
    export type UploadAttachmentContentByTypeMutationBody = UploadAttachmentContentByTypeBody
    export type UploadAttachmentContentByTypeMutationError = ErrorType<ProblemDetails>
    export type UploadAttachmentContentByTypeMutationVariables = {id: string;attachmentType: AttachmentType;data: UploadAttachmentContentByTypeBody}

    export const useUploadAttachmentContentByType = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useUploadAttachmentContentByTypeHook>>>, TError,UploadAttachmentContentByTypeMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useUploadAttachmentContentByTypeHook>>>,
        TError,
        UploadAttachmentContentByTypeMutationVariables,
        TContext
      > => {
      return useMutation(useUploadAttachmentContentByTypeMutationOptions(options), queryClient);
    }
